import clsx from 'clsx';
import React from 'react';

import ProExclusiveLogo from '@components/ProExclusiveLogo';
import ProjectTitle from '@components/ProjectTitle';
import useServerSideRendering from '@hooks/useServerSideRendering';
import { TI50Props } from '@interfaces/ti50';
import {
  article_path,
  ti50_database_path,
  ti_50_list_2020_path,
  ti_50_list_2021_path,
} from '@routes';
import { AutocompleteOption } from '@utils/interfaces';
import { AuthoredContentReactToHtmlParser } from '@utils/reactHtmlParserTransform';

import HeaderCategoryList from '../Header/HeaderCategoryList';
import Search from '../Search';

interface HeaderProps
  extends Omit<TI50Props, 'companyNames' | 'data' | 'paywallData' | 'TI50Year'> {
  companyNamesOptions: AutocompleteOption[];
}

const Header = ({
  activeCategories,
  companyNamesOptions,
  isSEOBot,
  project,
  unlocked,
}: HeaderProps) => {
  const hydrated = useServerSideRendering();

  return (
    <div className="px-12">
      <div className="mx-auto w-[1040px] max-w-full">
        {project.isPro && <ProExclusiveLogo />}
        <ProjectTitle
          className="!max-w-[670px]"
          title={project.title}
          titleCallout={project.titleCallout}
        />
        <h1 className="sr-only">{project.title}</h1>
        {unlocked || isSEOBot ? (
          <p
            className={clsx('mt-48 text-body-sm xs:text-body-lg', {
              'seo-paywall-content': isSEOBot,
            })}
            id="ti50-2022-description"
            data-nosnippet
          >
            {AuthoredContentReactToHtmlParser(project.description)}
          </p>
        ) : (
          <p className="mt-48 text-body-sm xs:text-body-lg" id="ti50-2022-description">
            Companies with the potential to be the most valuable businesses in their
            categories—based on current revenues, business models, and growth prospects.
          </p>
        )}
        {project.article?.publishedAt && (
          <p className="mt-32 text-body-sm xs:text-body-lg">
            Read{' '}
            <a
              className="font-bold underline hover:text-primary-magenta dark:hover:text-primary-pink"
              href={article_path(project.article.slug)}
            >
              {project.article.title}
            </a>
          </p>
        )}
        <p className="mt-32 text-body-sm xs:text-body-lg">
          Get the latest update: see{' '}
          <a
            className="font-bold underline hover:text-primary-magenta dark:hover:text-primary-pink"
            href={ti50_database_path()}
          >
            how these companies
          </a>{' '}
          have fared since making our list, amid economic uncertainty and a rise in new
          technologies.
        </p>
        <p className="mt-32 text-body-sm xs:text-body-lg">
          Past years:{' '}
          <a
            className="font-bold underline hover:text-primary-magenta dark:hover:text-primary-pink"
            href={ti_50_list_2021_path()}
          >
            The Information 50 2021
          </a>{' '}
          |{' '}
          <a
            className="font-bold underline hover:text-primary-magenta dark:hover:text-primary-pink"
            href={ti_50_list_2020_path()}
          >
            The Information 50 2020
          </a>{' '}
        </p>
        <HeaderCategoryList activeCategories={activeCategories} projectSlug={project.slug} />
        {hydrated && unlocked && <Search companyNamesOptions={companyNamesOptions} />}
      </div>
    </div>
  );
};

export default Header;
